import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper
} from '@mui/material';
import authService from '../../../api-authorization/AuthorizeService';

const EditNozzleProfileDialog = ({
  clientId,
  tractorId,
  nozzleProfileId,
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const { control, handleSubmit, watch, setValue } = useForm();
  const [booms, setBooms] = useState([]);
  const [nozzles, setNozzles] = useState([]);
  const [activeNozzleIds, setActiveNozzleIds] = useState([]);
  const [showNozzleError, setShowNozzleError] = useState(false); // NEW

  // Watch the name field for validation
  const nameValue = watch("name");

  // Fetch booms
  useEffect(() => {
    if (!open) return;
    const fetchBooms = async () => {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/booms?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        setBooms(await response.json());
      } else {
        setBooms([]);
      }
    };
    fetchBooms();
  }, [clientId, tractorId, open]);

  // Fetch nozzles
  useEffect(() => {
    if (!open) return;
    const fetchNozzles = async () => {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/nozzles?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const nozzleData = await response.json();
        setNozzles(nozzleData);
      } else {
        setNozzles([]);
      }
    };
    fetchNozzles();
  }, [clientId, tractorId, open]);

  // Fetch profile and set selected checkboxes
  useEffect(() => {
    if (!open || !nozzleProfileId) return;
    const fetchProfile = async () => {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/nozzle-profiles?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const profiles = await response.json();
        const profile = profiles.find(p => p.id === nozzleProfileId);
        if (profile) {
          setValue("name", profile.name);
          setValue("isDefault", profile.isDefault);
          setActiveNozzleIds(profile.nozzles ? profile.nozzles.map(n => n.id) : []);
        }
      }
    };
    fetchProfile();
  }, [clientId, tractorId, nozzleProfileId, open, setValue]);

  // Group nozzles by boom for position calculation
  const getNozzleRows = () => {
    // Map: boomId -> array of nozzles
    const grouped = {};
    nozzles.forEach((nozzle) => {
      if (!grouped[nozzle.tractorBoomId]) grouped[nozzle.tractorBoomId] = [];
      grouped[nozzle.tractorBoomId].push(nozzle);
    });

    // Build rows with position per boom
    const rows = [];
    Object.entries(grouped).forEach(([boomId, nozzleArr]) => {
      const boom = booms.find((b) => b.id === Number(boomId));
      nozzleArr.forEach((nozzle, idx) => {
        rows.push({
          nozzle,
          boomName: boom ? boom.name : '',
          position: idx + 1
        });
      });
    });
    return rows;
  };

  const handleActiveChange = (nozzleId, checked) => {
    setActiveNozzleIds((prev) =>
      checked ? [...prev, nozzleId] : prev.filter((id) => id !== nozzleId)
    );
  };

  // Select/Deselect all handler
  const handleSelectAll = (checked) => {
    if (checked) {
      setActiveNozzleIds(nozzles.map(n => n.id));
    } else {
      setActiveNozzleIds([]);
    }
  };

  const addNozzleProfile = async (data) => {
    // Show error if no nozzle is selected
    if (activeNozzleIds.length === 0) {
      setShowNozzleError(true);
      return;
    }
    setShowNozzleError(false);
    setLoading(true);
    const token = await authService.getAccessToken();

    // Send full TractorNozzle objects, not just IDs
    const selectedNozzles = nozzles.filter(n => activeNozzleIds.includes(n.id));

    const body = [
      {
        id: nozzleProfileId, // Pass the profile id here
        clientId: clientId,
        tractorId: tractorId,
        name: data.name,
        isDefault: data.isDefault,
        nozzles: selectedNozzles // full TractorNozzle objects
      }
    ];

    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/nozzle-profiles`,
      {
        method: 'POST',
        headers: !token
          ? {}
          : {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
        body: JSON.stringify(body)
      }
    );
    if (response.ok) {
      toast.success('Nozzle profile updated successfully!');
      onSuccess();
    } else {
      toast.error(`${response.status} ${response.statusText}`);
    }
    setLoading(false);
  };

  // Validation: name must be filled and at least one nozzle checked
  const canSave = !!nameValue && activeNozzleIds.length > 0;

  // Calculate total spray width of selected nozzles
  const totalSprayWidth = nozzles
    .filter(n => activeNozzleIds.includes(n.id))
    .reduce((sum, n) => sum + (parseFloat(n.sprayWidth) || 0), 0)
    .toFixed(1);

  // Determine if all are selected
  const allSelected = nozzles.length > 0 && activeNozzleIds.length === nozzles.length;
  const someSelected = activeNozzleIds.length > 0 && activeNozzleIds.length < nozzles.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <AddIcon />
        Edit Nozzle Profile
      </DialogTitle>
      <form onSubmit={handleSubmit(addNozzleProfile)}>
        <DialogContent >
          <Typography gutterBottom>
            Edit the nozzle profile for the sprayer.
          </Typography>
          <Typography variant="body2" color="info.main" sx={{ mb: 2 }}>
            Nozzle position goes from left to right, when looking at the back of the sprayer.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Controller
                control={control}
                name="name"
                rules={{ required: "Name is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    type="text"
                    disabled={loading}
                    InputProps={{ inputProps: { step: 'any' } }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    {...field}
                  />
                )}
              />
            </FormControl>
          </Box>
          {/* Table of nozzles */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Total Spray Width:&nbsp;
                <span style={{ fontWeight: 700 }}>{totalSprayWidth}</span>
              </Typography>
              <Box>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={e => handleSelectAll(e.target.checked)}
                  disabled={loading || nozzles.length === 0}
                  sx={{ p: 0, mr: 1 }}
                />
                <Typography variant="body2" component="span">
                  Select/Deselect All
                </Typography>
              </Box>
            </Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Select Nozzles for Profile
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Boom</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Width (in)</TableCell>
                    <TableCell>Active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getNozzleRows().map(({ nozzle, boomName, position }) => (
                    <TableRow key={nozzle.id}>
                      <TableCell>{boomName}</TableCell>
                      <TableCell>{position}</TableCell>
                      <TableCell>{nozzle.sprayWidth}</TableCell>
                      <TableCell>
                        <Checkbox
                          checked={activeNozzleIds.includes(nozzle.id)}
                          onChange={(e) =>
                            handleActiveChange(nozzle.id, e.target.checked)
                          }
                          disabled={loading}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Show error only after save is clicked */}
            {showNozzleError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                At least one nozzle must be selected.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={loading || !nameValue}
            type="submit"
          >
            Save Nozzle Profile
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditNozzleProfileDialog;
